import FastImage from '@d11/react-native-fast-image';
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS, setOpacity } from '../../constants';
import {
  selectDivRadius,
  selectMainColor,
} from '../../features/appconfig/store/selectors';
import { formatTimeFromSeconds } from '../../helpers/durationParser';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { formatEpisodeDate } from '../../localization/datetime';
import { PodcastEpisodeModel } from '../../models';
import CustomText from '../CustomText';
import { PlayPauseButton } from '../PlayPauseButton';

interface ResumeEpisodeCardProps {
  episode: PodcastEpisodeModel;
  position: number;
  duration: number;
  onPress?: () => void;
}

export type { ResumeEpisodeCardProps };

/**
 * Compact "resume listening" card for the podcast home (APPS-2160). Unlike the
 * generic EpisodeCard it sits on a solid card so it stays legible over the
 * screen's coloured background, and it surfaces the saved progress inline.
 */
export const ResumeEpisodeCard: React.FC<ResumeEpisodeCardProps> = ({
  episode,
  position,
  duration,
  onPress,
}) => {
  const divRadius = useAppSelector(selectDivRadius);
  const mainColor = useAppSelector(selectMainColor);
  const titleFontSize = useAdjustedFontSize(mvs(15));
  const dateFontSize = useAdjustedFontSize(mvs(11));

  const progressRatio = duration > 0 ? Math.min(position / duration, 1) : 0;
  const remainingSeconds = Math.max(duration - position, 0);

  return (
    <TouchableOpacity activeOpacity={0.95} onPress={onPress}>
      <View style={[styles.card, { borderRadius: mvs(divRadius) }]}>
        {/* thumbnail */}
        <View style={[styles.thumbnailContainer, { borderRadius: mvs(divRadius) * 0.7 }]}>
          <FastImage
            source={{ uri: episode.image, priority: FastImage.priority.normal }}
            style={styles.thumbnail}
            resizeMode={FastImage.resizeMode.cover}
          />
        </View>

        {/* title + date + progress */}
        <View style={styles.content}>
          <CustomText
            style={[styles.title, { fontSize: titleFontSize }]}
            weight="Bold"
            numberOfLines={2}
          >
            {episode.title}
          </CustomText>
          <CustomText
            style={[styles.date, { fontSize: dateFontSize }]}
            weight="SemiBold"
            numberOfLines={1}
          >
            {formatEpisodeDate(episode.date)}
          </CustomText>

          <View style={styles.progressRow}>
            <View style={styles.progressTrack}>
              <View
                style={[
                  styles.progressFill,
                  { width: `${progressRatio * 100}%`, backgroundColor: mainColor },
                ]}
              />
            </View>
            <CustomText
              style={[styles.remaining, { fontSize: dateFontSize }]}
              weight="SemiBold"
            >
              -{formatTimeFromSeconds(remainingSeconds)}
            </CustomText>
          </View>
        </View>

        {/* resume button (starts playback from the saved position) — same
            branded button as the episode cards below */}
        <PlayPauseButton
          size={45}
          iconSize={20}
          podcastEpisode={episode}
          style={styles.playButton}
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: COLORS.WHITE,
    flexDirection: 'row',
    alignItems: 'center',
    padding: mvs(10),
  },
  thumbnailContainer: {
    width: mvs(64),
    height: mvs(64),
    overflow: 'hidden',
    marginRight: mvs(12),
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    color: '#252525',
    lineHeight: mvs(19),
  },
  date: {
    color: '#9c9c9c',
    marginTop: mvs(2),
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(8),
    marginTop: mvs(8),
  },
  progressTrack: {
    flex: 1,
    height: mvs(4),
    borderRadius: mvs(2),
    backgroundColor: setOpacity('#252525', 12),
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: mvs(2),
  },
  remaining: {
    color: '#9c9c9c',
  },
  playButton: {
    marginLeft: mvs(12),
  },
});

export default ResumeEpisodeCard;
